import { MainLayoutContainer } from "components/Layout";
import { useRequirementDetail } from "./useRequirementDetail";
import {
  Box,
  CardContent,
  Stack,
  Typography,
  Grid2 as Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import LinkIcon from "@mui/icons-material/Link";
import { ResponsiveButtonLink } from "components/Button";
import { CardOutline } from "components/Card";
import { EvaluationMetricTable } from "components/EvaluationMetric";
import { Resource } from "types/resource";
import { RequirementBox, DomainColorSquares } from "components/Requirements";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { RequirementCriteria } from "components/Requirements/RequirementCriteria";
import { REQUIREMENT } from "helpers/constants";
import { TagManager } from "components/TagManager";
import { ROLES } from "types/user";
import { RequirementFeedbackStatsPanel } from "components/Analytics";
import { LineChartData } from "components/Analytics/RequirementFeedbackStatsPanel";
import { FeedbackDisplayModal } from "components/Feedback";
import { FeedbackType } from "types/feedback";

export const EPADetail = () => {
  const {
    requirement,
    requirementFeedbackStats,
    resources,
    competencies,
    role,
    isOrgView,
    organizationId,
    onClickDot,
    displayFeedback,
    setDisplayFeedback,
    feedbacks,
    customLabels,
  } = useRequirementDetail();
  if (!requirement) {
    return <div>loading...</div>;
  }
  return (
    <MainLayoutContainer
      title={requirement.name}
      breadcrumb={
        isOrgView
          ? BREADCRUMB_PAGE.ORGANIZATION_REQUIREMENT_EPAS
          : role === ROLES.ADMIN
            ? BREADCRUMB_PAGE.EPA_DETAIL
            : BREADCRUMB_PAGE.REQUIREMENT_PORTAL
      }
      buttonActions={
        <Stack direction="row" spacing={2} alignItems="center">
          {(role === ROLES.ADMIN ||
            role === ROLES.DIRECTOR ||
            role === ROLES.MANAGER) && (
            <ResponsiveButtonLink
              icon={<Edit />}
              to={
                organizationId
                  ? `/organization/${organizationId}/epa/${requirement.requirement_id}/edit`
                  : `/epa/${requirement.requirement_id}/edit`
              }
            >
              Edit
            </ResponsiveButtonLink>
          )}
        </Stack>
      }
    >
      <Box mt={2}>
        {role === ROLES.STUDENT &&
          requirementFeedbackStats &&
          requirementFeedbackStats?.totalFeedbacks > 0 && (
            <>
              <Typography variant="h6" mb={2}>
                Feedback Stats
              </Typography>
              <RequirementFeedbackStatsPanel
                totalFeedbacks={requirementFeedbackStats.totalFeedbacks}
                currentRate={requirementFeedbackStats.currentRate || 0}
                data={(requirementFeedbackStats.data as LineChartData[]) || []}
                onClickDot={onClickDot}
                onClickShowFeedbacks={() => setDisplayFeedback(true)}
              />
            </>
          )}
        {requirement?.description && (
          <Box my={2}>
            <Typography variant="h6">Description</Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
              {requirement?.description}
            </Typography>
          </Box>
        )}
        <Box mb={2}>
          <DomainColorSquares requirement={requirement} size={14} />
        </Box>
        <Grid container spacing={2}>
          <Grid size={{ xl: 4, xs: 12 }}>
            <RequirementCriteria
              requirement={requirement}
              type={REQUIREMENT.REQUIREMENT_EPA}
            />
          </Grid>
          {requirement.evaluation_metrics && (
            <Grid size={{ xl: 8, xs: 12 }}>
              <CardOutline sx={{ flexGrow: 1 }}>
                <CardContent>
                  <Typography gutterBottom component="div" fontWeight={600}>
                    Evaluation Metric
                  </Typography>
                  <Box>
                    <EvaluationMetricTable
                      requirement={requirement}
                      evaluationMetrics={requirement.evaluation_metrics}
                      customLabels={customLabels}
                    />
                  </Box>
                </CardContent>
              </CardOutline>
            </Grid>
          )}
          <Grid size={12}>
            <Box mt={2}>
              <CardOutline>
                <Box p={2}>
                  <Typography gutterBottom component="div" fontWeight={600}>
                    Related Competency Skills
                  </Typography>
                  {competencies.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                      No related competency skills
                    </Typography>
                  )}
                  {competencies.map((requirement) => (
                    <RequirementBox
                      key={requirement.requirement_id}
                      requirement={requirement}
                    />
                  ))}
                </Box>
              </CardOutline>
            </Box>
          </Grid>
          <Grid size={12}>
            <Box mt={2}>
              <CardOutline>
                <Box p={2}>
                  <Typography gutterBottom component="div" fontWeight={600}>
                    Additional Resources
                  </Typography>
                  {resources.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                      No additional resources
                    </Typography>
                  )}
                  {resources ? (
                    <List>
                      {resources?.map((resource: Resource) => (
                        <ListItem key={resource.resource_id}>
                          <ListItemButton
                            component="a"
                            target="_blank"
                            href={resource.url}
                          >
                            <ListItemText
                              primary={resource.name}
                              secondary={resource.description}
                            />
                            <ListItemIcon>
                              <LinkIcon />
                            </ListItemIcon>
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2">
                      No additional resources
                    </Typography>
                  )}
                </Box>
              </CardOutline>
            </Box>
          </Grid>

          <Grid size={12}>
            <Box mt={2}>
              <CardOutline>
                <Box mt={2} p={2}>
                  <Stack spacing={1} direction={"row"}>
                    <Typography gutterBottom component="div" fontWeight={600}>
                      Tags:
                    </Typography>
                    {requirement?.tags && requirement.tags.length === 0 && (
                      <Typography variant="body1" color="textSecondary">
                        No tags
                      </Typography>
                    )}
                    {requirement?.tags && (
                      <TagManager
                        tags={requirement?.tags.map((t) => t.tag)}
                        isEditing={false}
                      />
                    )}
                  </Stack>
                </Box>
              </CardOutline>
            </Box>
          </Grid>
        </Grid>
        <FeedbackDisplayModal
          open={displayFeedback}
          onClose={() => setDisplayFeedback(false)}
          selectedStatusLabel=""
          filteredData={feedbacks as FeedbackType[]}
          displayStudentName={false}
        />
      </Box>
    </MainLayoutContainer>
  );
};
